import React, { useRef, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, ArrowUpRight, ShieldCheck, Home as HomeIcon, HeartHandshake, Users,
  HeartPulse, FileText, Briefcase, Shield, Check, CheckCircle2, MapPin, ClipboardCheck, Sparkles, Award, Quote,
} from "lucide-react";
import { Reveal } from "../components/Reveal";
import { HowItWorks } from "../components/HowItWorks";
import { Marquee } from "../components/Marquee";
import { CTABand } from "../components/CTABand";
import { IntakeQuizModal, screenerToPrefill } from "../components/IntakeQuizModal";
import { FaqSection } from "../components/FaqSection";
import {
  PROGRAMS, TRUST_STRIP, HERO_TRUST, WHO_WE_SERVE, STANDARD_CARDS, STATS, TESTIMONIALS, IMAGES, SITE,
} from "../data/content";

const trustIcons = [ShieldCheck, HomeIcon, HeartHandshake, Users, HeartPulse];
const iconMap = {
  home: HomeIcon, "heart-pulse": HeartPulse, "file-text": FileText, briefcase: Briefcase,
  shield: Shield, users: Users,
};

const CenteredHeading = ({ eyebrow, title, intro, light = false }) => (
  <div className="max-w-3xl mx-auto text-center">
    {eyebrow && (
      <Reveal>
        <span className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 label-caps ${light ? "border-offwhite/20 text-gold" : "border-softgray bg-white text-navy"}`}>
          <Sparkles className="h-3.5 w-3.5 text-gold" /> {eyebrow}
        </span>
      </Reveal>
    )}
    <Reveal delay={0.05}>
      <h2 className={`mt-6 text-3xl md:text-4xl lg:text-5xl font-black tracking-tight leading-[1.1] ${light ? "text-offwhite" : "text-navy"}`}>{title}</h2>
    </Reveal>
    {intro && (
      <Reveal delay={0.1}>
        <p className={`mt-4 text-base md:text-lg leading-relaxed ${light ? "text-offwhite/70" : "text-charcoal/80"}`}>{intro}</p>
      </Reveal>
    )}
  </div>
);

const Hero = ({ onScreener }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 80]);

  return (
    <section ref={ref} className="relative pt-[120px] md:pt-[140px] pb-12 md:pb-16 bg-white overflow-hidden" data-testid="hero">
      <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 right-0 z-0 hidden md:flex items-center justify-end overflow-hidden">
        <svg viewBox="0 0 100 120" className="h-[72%] w-auto translate-x-[30%] text-navy/[0.045]" fill="currentColor">
          <path d="M50 2 L94 18 V58 C94 91 74 112 50 118 C26 112 6 91 6 58 V18 Z" />
        </svg>
      </div>
      <div className="relative z-10 container-x grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center">
        <div className="lg:col-span-6">
          <div className="flex items-center gap-3 mb-7">
            <span className="h-px w-10 bg-gold" />
            <span className="label-caps text-navy">Est. North Carolina</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-[4rem] font-black tracking-tight text-navy leading-[1.05]">
            <span className="block">Safe reset housing for</span>
            <span className="block">veterans in crisis.</span>
          </h1>

          <p className="mt-7 max-w-xl text-base md:text-lg text-charcoal/80 leading-relaxed">
            Iron Shield provides structured transitional housing, stabilization support, and recovery-centered guidance for homeless and at-risk veterans.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row flex-wrap gap-3">
            <Link to="/admissions" data-testid="hero-gethelp" className="group inline-flex items-center justify-center gap-2 bg-navy text-offwhite px-7 py-4 rounded-md text-sm font-semibold uppercase tracking-wide hover:bg-navy-deep transition-colors">
              <ShieldCheck className="h-4 w-4" /> Start Intake Form <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to="/partners" data-testid="hero-refer" className="inline-flex items-center justify-center gap-2 border border-softgray bg-white text-navy px-7 py-4 rounded-md text-sm font-semibold uppercase tracking-wide hover:border-navy transition-colors">
              Refer a Veteran
            </Link>
            <button type="button" onClick={onScreener} data-testid="hero-screener" className="inline-flex items-center justify-center gap-2 bg-gold/15 text-gold-deep px-7 py-4 rounded-md text-sm font-semibold uppercase tracking-wide hover:bg-gold/25 transition-colors">
              <Sparkles className="h-4 w-4" /> Quick Screener
            </button>
          </div>

          <div className="mt-8 flex flex-wrap md:flex-nowrap gap-x-7 gap-y-3 border-t border-softgray pt-6">
            {HERO_TRUST.map((t) => (
              <span key={t} className="inline-flex items-center gap-2 text-sm font-medium text-charcoal whitespace-nowrap">
                <CheckCircle2 className="h-[18px] w-[18px] text-navy shrink-0" strokeWidth={2} /> {t}
              </span>
            ))}
          </div>
        </div>

        <div className="lg:col-span-6 lg:pl-6">
          <motion.div style={{ y }} className="relative">
            <div className="rounded-[22px] bg-white p-2.5 pb-5 shadow-[0_45px_90px_-35px_rgba(18,62,124,0.4)] ring-1 ring-softgray/60">
              <motion.div
                initial={{ clipPath: "inset(0 0 100% 0)" }}
                animate={{ clipPath: "inset(0 0 0% 0)" }}
                transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.35 }}
                className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl"
              >
                <img src={IMAGES.heroSupport} alt="A veteran meeting with an Iron Shield case manager during intake" className="h-full w-full object-cover object-center" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/75 via-navy-deep/5 to-transparent" />
              </motion.div>
            </div>

            <div className="absolute -top-5 left-5 md:left-7 z-20 flex items-center gap-3 rounded-xl bg-navy border border-gold/60 px-4 py-3 shadow-lg" data-testid="hero-badge">
              <Award className="h-6 w-6 text-gold shrink-0" strokeWidth={1.75} />
              <div className="leading-tight">
                <div className="font-heading text-sm font-black text-gold">Veterans First</div>
                <div className="text-xs font-semibold text-offwhite/70">Dedicated Housing</div>
              </div>
            </div>

            <div
              className="absolute left-3 right-3 bottom-3 md:left-6 md:right-6 md:bottom-6 z-10 bg-white border border-softgray rounded-lg md:rounded-xl p-3 md:p-5 flex items-center justify-between gap-3 shadow-lg"
              data-testid="hero-facility-card"
            >
              <div className="min-w-0">
                <div className="label-caps text-navy flex items-center gap-1.5 text-[10px] md:text-xs"><MapPin className="h-3 w-3 md:h-3.5 md:w-3.5 text-gold shrink-0" /> Residential Campus Facility</div>
                <div className="mt-0.5 md:mt-1 font-heading text-sm md:text-lg font-bold text-navy leading-snug">{SITE.campus}</div>
              </div>
              <span className="shrink-0 rounded-md md:rounded-lg bg-navy px-2.5 py-1 md:px-3 md:py-1.5 text-[10px] md:text-xs font-bold text-gold">{SITE.region}</span>
            </div>

            <div className="absolute -top-3 -right-3 h-16 w-16 border-t-2 border-r-2 border-gold" aria-hidden="true" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const Testimonials = () => (
  <section className="py-20 md:py-28 bg-white border-b border-softgray" data-testid="testimonials">
    <div className="container-x">
      <CenteredHeading
        eyebrow="Voices of the mission"
        title="Trust, earned one veteran at a time"
        intro="Representative stories from the veterans, case managers, and families we serve."
      />
      <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
        {TESTIMONIALS.map((t, i) => (
          <Reveal key={t.name} delay={i * 0.1} className="i-card p-8 h-full flex flex-col" data-testid={`testimonial-${i}`}>
            <Quote className="h-8 w-8 text-gold shrink-0" strokeWidth={1.5} />
            <p className="mt-5 text-charcoal/85 leading-relaxed text-[15px] flex-1">"{t.quote}"</p>
            <div className="mt-6 pt-5 border-t border-softgray">
              <div className="font-heading text-base font-bold text-navy">{t.name}</div>
              <div className="mt-0.5 text-sm text-slate">{t.role}</div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const TrustStrip = () => (
  <section className="border-y border-navy/20 bg-navy" data-testid="trust-strip">
    <div className="container-x py-5 md:py-6">
      <div className="flex flex-wrap items-center justify-center gap-x-8 md:gap-x-12 gap-y-4">
        {TRUST_STRIP.map((item, i) => {
          const Icon = trustIcons[i];
          return (
            <motion.div
              key={item}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              className="flex items-center gap-2.5"
            >
              <Icon className="h-5 w-5 text-gold shrink-0" strokeWidth={1.75} />
              <span className="text-xs md:text-sm font-semibold text-offwhite uppercase tracking-wide whitespace-nowrap">{item}</span>
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);

const ProgramsPreview = () => (
  <section className="py-20 md:py-28 bg-offwhite" data-testid="programs-preview">
    <div className="container-x">
      <CenteredHeading
        eyebrow="Structured Stabilization Path"
        title="Core Programs & Reset Pathways"
        intro="Every program is designed to move veterans from immediate crisis to long-term independence."
      />
      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {PROGRAMS.map((p, i) => {
          const Icon = iconMap[p.icon];
          return (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: i * 0.08 }}
              data-testid={`program-card-${p.id}`}
            >
              <Link to={`/programs#${p.id}`} className="group i-card flex h-full flex-col p-7">
                <div className="font-heading text-sm font-black text-gold">{p.num}</div>
                <div className="mt-4 h-12 w-12 flex items-center justify-center border border-softgray bg-offwhite text-navy group-hover:bg-navy group-hover:text-offwhite group-hover:border-navy transition-colors">
                  <Icon className="h-5 w-5" strokeWidth={1.75} />
                </div>
                <h3 className="mt-5 font-heading text-lg font-bold text-navy uppercase tracking-tight">{p.title}</h3>
                <p className="mt-3 text-[15px] text-charcoal/80 leading-relaxed">{p.desc}</p>
                <div className="mt-6 pt-5 border-t border-softgray flex items-center justify-between label-caps text-navy group-hover:text-gold-deep transition-colors">
                  View details
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </div>
              </Link>
            </motion.div>
          );
        })}
      </div>
      <Reveal delay={0.1} className="mt-12 flex justify-center">
        <Link to="/programs" data-testid="programs-viewall" className="group inline-flex items-center gap-2 border border-navy text-navy px-8 py-4 rounded-sm text-sm font-semibold hover:bg-navy hover:text-offwhite transition-colors">
          View Complete Programs & Eligibility Criteria
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </div>
  </section>
);

const HowItWorksSection = () => (
  <section className="py-20 md:py-28 bg-white border-y border-softgray" data-testid="how-it-works">
    <div className="container-x">
      <CenteredHeading
        eyebrow="Transparent Process"
        title="How It Works: 3 Steps to Reset"
        intro="A transparent, supportive journey designed to eliminate confusion for veterans and case managers."
      />
      <HowItWorks />
      <Reveal delay={0.1} className="mt-14 flex justify-center">
        <Link to="/admissions" data-testid="how-cta" className="group inline-flex items-center gap-2 bg-navy text-offwhite px-8 py-4 rounded-sm text-sm font-semibold hover:bg-navy-deep transition-colors">
          <ShieldCheck className="h-4 w-4" /> Start Intake Form
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </div>
  </section>
);

const WhoWeServe = () => (
  <section className="py-20 md:py-28 bg-offwhite" data-testid="who-we-serve">
    <div className="container-x">
      <CenteredHeading
        eyebrow="Who We Serve"
        title="Support at every stage of risk"
        intro="Tailored support programs for veterans at every stage of housing risk and transition."
      />
      <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
        {WHO_WE_SERVE.map((w, i) => {
          const Icon = iconMap[w.icon];
          return (
            <Reveal key={w.title} delay={i * 0.1} className="group i-card p-8 h-full">
              <div className="h-12 w-12 flex items-center justify-center border border-softgray bg-offwhite text-navy group-hover:bg-navy group-hover:text-offwhite group-hover:border-navy transition-colors">
                <Icon className="h-5 w-5" strokeWidth={1.75} />
              </div>
              <h3 className="mt-6 font-heading text-lg font-bold text-navy uppercase tracking-tight">{w.title}</h3>
              <p className="mt-3 text-charcoal/80 leading-relaxed text-[15px]">{w.desc}</p>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const IronShieldStandard = () => (
  <section className="py-20 md:py-28 bg-navy text-offwhite" data-testid="why-iron-shield">
    <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
      <div className="lg:col-span-5 lg:sticky lg:top-28">
        <Reveal>
          <span className="inline-flex items-center gap-2 rounded-full border border-offwhite/20 px-4 py-1.5 label-caps text-gold">The Iron Shield Standard</span>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-6 text-3xl md:text-4xl lg:text-5xl font-black tracking-tight leading-[1.08] text-offwhite">
            Built on Safety, Structure, Dignity & Accountability
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 text-offwhite/70 leading-relaxed max-w-lg">
            We provide a respectful, highly organized campus where veterans recover their momentum among peers who understand military service — grounded in trauma-informed care and a zero-tolerance safety standard.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <Link to="/about" data-testid="standard-learnmore" className="group mt-8 inline-flex items-center gap-2 label-caps text-gold hover:text-offwhite transition-colors">
            Learn our story <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>

      <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
        {STANDARD_CARDS.map((c, i) => (
          <Reveal key={c.title} delay={i * 0.06}>
            <div className="i-card-dark h-full p-7">
              <div className="flex items-start gap-3">
                <ClipboardCheck className="h-5 w-5 text-gold shrink-0 mt-0.5" strokeWidth={1.75} />
                <div>
                  <h3 className="font-heading text-base font-bold text-gold">{c.title}</h3>
                  <p className="mt-2 text-[15px] text-offwhite/70 leading-relaxed">{c.desc}</p>
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Stats = () => (
  <section className="bg-white border-b border-softgray py-16 md:py-20" data-testid="stats">
    <div className="container-x">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-y-10 md:gap-y-0 md:divide-x md:divide-softgray">
        {STATS.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.08} className="text-center px-4">
            <div className="font-heading text-4xl md:text-5xl font-black text-navy tracking-tight">{s.value}</div>
            <div className="mx-auto mt-3 h-0.5 w-8 bg-gold" />
            <div className="mt-4 text-sm md:text-base font-bold text-charcoal">{s.label}</div>
            <div className="mt-1 text-xs text-slate uppercase tracking-wide">{s.note}</div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Home = () => {
  const navigate = useNavigate();
  const [screenerOpen, setScreenerOpen] = useState(false);
  useEffect(() => { document.title = "Iron Shield Veterans Reset Center | Transitional Support for Veterans"; }, []);

  const handleScreenerComplete = (answers) => {
    navigate("/admissions", { state: { prefill: screenerToPrefill(answers) } });
  };

  return (
    <>
      <Hero onScreener={() => setScreenerOpen(true)} />
      <TrustStrip />
      <Testimonials />
      <ProgramsPreview />
      <Marquee items={["Safety", "Structure", "Dignity", "Accountability", "Reintegration"]} />
      <HowItWorksSection />
      <WhoWeServe />
      <IronShieldStandard />
      <Stats />
      <FaqSection />
      <CTABand />
      <IntakeQuizModal open={screenerOpen} onClose={() => setScreenerOpen(false)} onComplete={handleScreenerComplete} />
    </>
  );
};

export default Home;
